import { reactive } from 'vue';
import type { ColumnApi, GridApi } from 'ag-grid-community';
import type { IGridExposed, IGridProps } from '@platform/shared/components/grid/types';
import { v4 as guid } from 'uuid';

export type { IGridProps };
export type IGridPartialProps = Omit<IGridProps, 'gridId'>;

export type RequiredGridParams = {
    id: string;
    fetching: boolean;
    created: Date;
    schemaName?: string;
    tableName?: string;
    name?: string;
    componentName?: string | null;
    logging: boolean;
    keepAlive: boolean;
    crudFunctionAdd?: (val?: any[] | boolean) => void | any;
};
export type ReadyGridParams = {
    ref: IGridExposed;
    gridTransactionId: string;
    getGridApi: () => GridApi;
    getGridColumnApi: () => ColumnApi;
    gridFunctions: {
        onQuickFilterChanged: (val: string) => void;
        loadRowData: (source?: any, omitLoadingStatus?: boolean) => Promise<void>;
        setColumnDefs: (source?: any, omitLoadingStatus?: boolean) => Promise<void>;
        setLoadingStatus: (status: boolean) => void;
        updateRows: (event: any, client_grid_hierarchy_column: string[], setFocus: boolean | null | undefined) => void;
        applyGridConfiguration: () => void;
        setGridDataChanged: (changed: boolean) => void;
        getGridDataChanged: () => boolean | null;
    };
    gridCurrentRow: {
        colId: string | null | undefined;
        id: string | null | undefined;
        rowIndex: number;
    } | null;
    oldValue?: any | null;
};

export type GridParams = RequiredGridParams & ({ ready: false } | ({ ready: true } & ReadyGridParams));

const gridStore = reactive<Record<string, GridParams>>({});

type ReadyHandler = (gridParams: RequiredGridParams & { ready: true } & ReadyGridParams) => void;
const hooks = {
    ready: [] as { gridId: string; handler: ReadyHandler }[],
} as const;
const runHook = <T extends keyof typeof hooks>(
    state: T,
    gridId: string,
    ...args: Parameters<(typeof hooks)[T][number]['handler']>
) => {
    for (let i = hooks[state].length - 1; i >= 0; i--) {
        if (hooks[state][i].gridId === gridId) {
            try {
                hooks[state][i].handler.apply(null, args);
            } catch (e) {
                console.error(e);
            } finally {
                hooks[state].splice(i, 1);
            }
        }
    }
};
const LoggingEnabled = true;
export default {
    init(params: Partial<GridParams> = {}, id = guid().toLocaleLowerCase()): GridParams {
        if (id in gridStore) {
            console.warn(`Grid tried to initiate itself twice. Grid ID: ${id}`);
            throw new Error(`That should not occur.`);
        }
        gridStore[id] = {
            fetching: false,
            logging: false,
            keepAlive: false,
            ...params,
            id,
            ready: false,
            created: new Date(),
        };
        if (params.logging || LoggingEnabled) console.debug('init grid', id, gridStore[id], gridStore);
        return gridStore[id];
    },
    setParams(
        id: string,
        params: Partial<Pick<RequiredGridParams, 'fetching' | 'tableName'> | ReadyGridParams>,
        markAsReady?: boolean
    ) {
        if (!(id in gridStore)) {
            console.warn(`Grid with id ${id} does not exists.`);
            return;
        }
        if (LoggingEnabled) {
            console.debug('set params to grid', id, params, 'markAsReady=', markAsReady);
        }
        gridStore[id] = { ...gridStore[id], ...params };
        if (markAsReady && !gridStore[id].ready) {
            gridStore[id].ready = true;
            runHook('ready', id, gridStore[id] as RequiredGridParams & { ready: true } & ReadyGridParams);
        }
        if (gridStore[id].logging) console.debug('set grid', id, gridStore[id]);
    },
    getParams(id: string): GridParams | null {
        return gridStore[id] || {};
    },
    _initializeFromGridItself(id: string, params: Partial<RequiredGridParams & ReadyGridParams>) {
        this.setParams(id, params, false);
    },
    destroy(id: string, destroyAnyway = false) {
        if (id in gridStore) {
            if (gridStore[id].logging) console.debug('destroy grid', id, 'keep-alive: ' + gridStore[id].keepAlive);
            if (gridStore[id].keepAlive && !destroyAnyway) return;
            delete gridStore[id];
        }
    },
    waitUntil<T extends keyof typeof hooks>(state: T, gridId: string, handler: (typeof hooks)[T][number]['handler']) {
        hooks[state].push({ gridId, handler });
        return () => {
            const index = hooks[state].findIndex((hook) => hook.gridId === gridId && hook.handler === handler);
            return index !== -1 && hooks[state].splice(index, 1);
        };
    },
};
